import * as React from 'react';
import { searchAPIClient } from '@/scopes/search/search/infrastructure/api';
import type { CampaignDetailsApiClient } from '@/scopes/search/search/domain/campaign-details/types';
import { createCampaignDetailsApiClient } from '@/scopes/search/search/infrastructure/campaign-details/campaignDetailsApiClient';
import { filterSelectionQueryParamHandler } from '@/scopes/search/search/domain/filterSelectionQueryParamHandler';
import { searchStore } from '@/scopes/search/search/infrastructure/store/store';
import { createBrowserQueryStringKeepingParams } from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams';
import { searchPassThroughParams, type SearchPassThroughParams } from '@/scopes/search/types';
import type { QueryStringClient } from '@/scopes/common/infrastructure/query-strings/queryStringClient';

const queryStringClientEmptyImplementation = {
  set: () => {
    throw new Error('Not implemented');
  },
  get: () => {
    throw new Error('Not implemented');
  },
  getValue: () => {
    throw new Error('Not implemented');
  },
  remove: () => {
    throw new Error('Not implemented');
  },
} as QueryStringClient;

const SearchDependenciesContext = React.createContext<SearchDependencies>({
  searchAPIClient,
  campaignDetailsApiClient: createCampaignDetailsApiClient({ contextItemId: '' }),
  filterSelectionQueryParamHandler: filterSelectionQueryParamHandler({
    store: searchStore,
    browserQuery: createBrowserQueryStringKeepingParams<SearchPassThroughParams>({
      passThroughParams: searchPassThroughParams,
      queryStringClient: queryStringClientEmptyImplementation,
    }),
  }),
});

interface SearchDependencies {
  searchAPIClient: typeof searchAPIClient;
  campaignDetailsApiClient: CampaignDetailsApiClient;
  filterSelectionQueryParamHandler: ReturnType<typeof filterSelectionQueryParamHandler>;
}

export const SearchDependenciesProvider = ({
  children,
  dependencies,
}: React.PropsWithChildren<{ dependencies: SearchDependencies }>) => {
  return <SearchDependenciesContext.Provider value={dependencies}>{children}</SearchDependenciesContext.Provider>;
};

export const useSearchDependencies = () => React.useContext(SearchDependenciesContext);
